<template>
  <TransitionRoot as="template" :show="open">
    <Dialog class="relative z-50" @close="closeDialog">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6">
              <div>
                <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div class="mt-3 text-center sm:mt-5">
                  <DialogTitle as="h3" class="text-lg font-semibold leading-6 text-gray-900">Histórico de Ajustes Manuais</DialogTitle>
                  <p class="text-sm text-gray-500">
                    Segurado: <span class="font-bold text-gray-700">{{ movimentacao?.segurado_nome }}</span>
                  </p>
                </div>
              </div>

              <div class="mt-6">
                <div v-if="loading" class="flex justify-center py-10">
                  <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
                
                <div v-else-if="historico.length === 0" class="text-center py-10 text-gray-500 italic">
                  Nenhum ajuste manual registrado para esta movimentação.
                </div>

                <div v-else class="overflow-x-auto">
                  <table class="min-w-full divide-y divide-gray-300">
                    <thead>
                      <tr>
                        <th class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Data</th>
                        <th class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Responsável</th>
                        <th class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Campo</th>
                        <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Anterior</th>
                        <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Novo</th>
                        <th class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Justificativa</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-200">
                      <tr v-for="item in historico" :key="item.id">
                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {{ formatDate(item.created_at) }}
                        </td>
                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                          {{ item.responsavel_email }}
                        </td>
                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {{ formatCampo(item.campo) }}
                        </td>
                        <td class="whitespace-nowrap px-3 py-4 text-sm text-right text-red-600 font-mono">
                          {{ formatCurrency(item.valor_anterior) }}
                        </td>
                        <td class="whitespace-nowrap px-3 py-4 text-sm text-right text-green-600 font-bold font-mono">
                          {{ formatCurrency(item.valor_novo) }}
                        </td>
                        <td class="px-3 py-4 text-sm text-gray-500 max-w-xs break-words">
                          {{ item.justificativa }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div class="mt-8 flex justify-end">
                <button type="button" @click="closeDialog" class="rounded-md bg-white px-3.5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-colors">
                  Fechar
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import ControleMovimentosRepository from '@/services/ControleMovimentosRepository.vue';

export default {
  name: 'HistoricoAjusteModal',
  components: {
    Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot
  },
  props: {
    open: { type: Boolean, required: true },
    movimentacao: { type: Object, required: false, default: null }
  },
  data() {
    return {
      loading: false,
      historico: []
    };
  },
  watch: {
    open(val) {
      if (val && this.movimentacao?.id) {
        this.fetchHistorico();
      }
    }
  },
  methods: {
    closeDialog() {
      this.$emit('update:open', false);
    },
    async fetchHistorico() {
      this.loading = true;
      const repo = new ControleMovimentosRepository();
      const user = JSON.parse(window.localStorage.getItem("myUser"));
      try {
        const response = await repo.getHistoricoAjustes(user.token, this.movimentacao.id);
        this.historico = response.data;
      } catch (error) {
        console.error("Erro ao buscar histórico:", error);
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      if (!date) return '-';
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(new Date(date));
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
    },
    formatCampo(campo) {
      const names = {
        'valor_capital_novo': 'Capital',
        'valor_mensal_novo': 'Custo Mensal',
        'valor_premio_novo': 'Prêmio'
      };
      return names[campo] || campo;
    }
  }
};
</script>
